import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  createCourse, 
  clearFormErrors, 
  resetSubmissionStatus,
  selectCourseCreateStatus,
  selectCourseCreateError,
  selectCourseFormErrors,
} from '../../features/courses/courseSlice';
import { selectUserInformations } from '../../features/authSlice';
import { selectCategoryData } from '../../features/categories/categorySlice';
import { selectInstructorData } from '../../features/instructors/instructorSlice';
import SharedForm from '../../components/forms/SharedForm';
import courseFields from '../../config/courseFields';
import { showToast } from '../../utils/utils';

/**
 * Create Course Page
 * Full page component for creating a new course
 */
const CreateCoursePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const categories = useSelector(selectCategoryData);
  const instructors = useSelector(selectInstructorData);
  
  const createStatus = useSelector(selectCourseCreateStatus);
  const createError = useSelector(selectCourseCreateError);
  const formErrors = useSelector(selectCourseFormErrors);

  const [formData, setFormData] = useState(
    courseFields.reduce(
      (acc, field) => ({ ...acc, [field.name]: field.defaultValue || '' }),
      {}
    )
  );

  // Update courseFields with categories and instructors
  const fieldsWithOptions = courseFields.map(field => {
    if (field.name === 'category_id' && categories?.items) {
      return {
        ...field,
        options: categories.items.map(cat => ({ value: cat.id, label: cat.title }))
      };
    }
    if (field.name === 'instructor_id' && instructors?.items) {
      return {
        ...field,
        options: instructors.items.map(inst => ({ value: inst.id, label: inst.name || `${inst.first_name} ${inst.last_name}` }))
      };
    }
    return field;
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'image_url' ? files?.[0] : value,
    }));
    
    if (formErrors[name]) {
      dispatch(clearFormErrors());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(createCourse({ 
      token: user.token, 
      formData 
    }));
  };

  useEffect(() => {
    if (createStatus === 'succeeded') {
      showToast('Success', 'Course created successfully', 'success');
      dispatch(resetSubmissionStatus());
      navigate('/dashboard/courses', { replace: true });
    }
  }, [createStatus, dispatch, navigate]);

  useEffect(() => {
    if (createStatus === 'failed' && createError) {
      showToast('Error', createError, 'error');
    }
  }, [createStatus, createError]);

  useEffect(() => {
    return () => {
      dispatch(resetSubmissionStatus());
    };
  }, [dispatch]);

  const loading = createStatus === 'loading';

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Create New Course
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Fill in the form below to create a new course
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard/courses')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </motion.div>

        {(createError || Object.values(formErrors).some(Boolean)) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800"
          >
            {createError && (
              <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-2">
                {createError}
              </p>
            )}
            {Object.entries(formErrors).map(([field, errorMsg]) => (
              errorMsg && (
                <p key={field} className="text-red-600 dark:text-red-400 text-sm">
                  {field}: {errorMsg}
                </p>
              )
            ))}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <SharedForm
            fields={fieldsWithOptions}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={loading}
            hasCategories={true}
            hasInstructors={true}
            entity="course"
            formErrors={formErrors}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default CreateCoursePage;
